/**
 * ClinicsReportTable — платформенный свод: клиника, выручка нетто, долг,
 * приёмы (завершено/всего), врачи, пациенты. Клик — детальные отчёты.
 */

import {Building2, ChevronRight} from 'lucide-react';

import type {ClinicReportRow} from '@services/reports';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@components/ui/Table';
import {formatNumber} from '@utils/format';

const th = 'text-[11px] font-semibold uppercase tracking-[.04em] text-skr-label';

type Props = {
  rows: ClinicReportRow[];
  onOpen: (id: string) => void;
};

export const ClinicsReportTable = ({rows, onOpen}: Props) => (
  <div className="overflow-x-auto">
    <Table className="min-w-[860px] text-[13.5px]">
      <TableHeader>
        <TableRow className="border-b border-skr-line2 bg-skr-head hover:bg-skr-head">
          <TableHead className={th}>Клиника</TableHead>
          <TableHead className={`${th} text-right`}>Выручка нетто, сом</TableHead>
          <TableHead className={`${th} text-right`}>Долг, сом</TableHead>
          <TableHead className={`${th} text-right`}>Приёмы</TableHead>
          <TableHead className={`${th} text-right`}>Врачи</TableHead>
          <TableHead className={`${th} text-right`}>Пациенты</TableHead>
          <TableHead className="w-9" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow
            key={r.clinicId}
            onClick={() => onOpen(r.clinicId)}
            className="cursor-pointer border-b border-skr-hairline hover:bg-skr-head"
          >
            <TableCell>
              <div className="flex items-center gap-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-[10px] bg-skr-icon-bg text-muted-foreground">
                  <Building2 className="size-[17px]" strokeWidth={1.8} />
                </span>
                <div className="leading-[1.35]">
                  <div className="font-semibold text-foreground">{r.name}</div>
                  <div className="text-xs text-muted-foreground">{r.cityName}</div>
                </div>
              </div>
            </TableCell>
            <TableCell className="tnum text-right font-mono font-medium text-foreground">
              {formatNumber(r.revenueNet)}
            </TableCell>
            <TableCell
              className={`tnum text-right font-mono ${r.debt > 0 ? 'text-skr-warn' : 'text-skr-faint'}`}
            >
              {formatNumber(r.debt)}
            </TableCell>
            <TableCell className="tnum text-right font-mono text-skr-ink">
              {r.completed}
              <span className="text-skr-faint"> / {r.appointments}</span>
            </TableCell>
            <TableCell className="tnum text-right font-mono text-skr-ink">{r.doctors}</TableCell>
            <TableCell className="tnum text-right font-mono text-skr-ink">
              {formatNumber(r.patients)}
            </TableCell>
            <TableCell className="pr-2">
              <ChevronRight className="size-4 text-skr-chevron" strokeWidth={2} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);
